<template>
    <footer
        v-qa.hydrated="'hydrated'"
        data-qa="footer"
        class="footer"
    >
        <div class="footer-wrapper">
            <nav class="footer-navigation">
                <FooterMenu
                    v-for="(column, index) in data.columns"
                    :key="index"
                    :data="column"
                />

                <div
                    v-if="!hideSwitchers"
                    class="switchers"
                >
                    <CurrencySwitcher
                        v-qa="'currency-switcher'"
                        class="currency-switcher"
                        is-in-footer
                    />

                    <RegionSwitcher
                        v-qa="'region-switcher'"
                        class="region-switcher"
                        is-in-footer
                    />
                </div>
            </nav>

            <div class="footer-bar">
                <div class="copyright">
                    {{ data.copyright }}
                </div>

                <div class="social">
                    <SocialIcon
                        v-for="(socialNetwork, key) in socialNetworks"
                        :key="key"
                        :data="socialNetwork"
                    />
                </div>

                <div class="payment">
                    <PaymentIcon
                        v-for="(icon, key) in paymentIcons"
                        :key="key"
                        :data="icon"
                    />
                </div>
            </div>
        </div>
        <FooterQANavigationDummy />
    </footer>
</template>

<script setup lang="ts">
import { useIsOnRoute } from '@/composables/useIsOnRoute'

import type {
    FooterFragment,
    PaymentIconFragment,
    SocialNetworkFragment,
} from '@/graphql/api'

import {
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
} from '@/router'

import FooterMenu from './FooterMenu.vue'
import SocialIcon from './SocialIcon.vue'
import PaymentIcon from './PaymentIcon.vue'
import FooterQANavigationDummy from '@/components/qa/FooterQANavigationDummy.vue'
import CurrencySwitcher from '../../app/CurrencySwitcher.vue'
import RegionSwitcher from '../../app/RegionSwitcher.vue'

defineProps<{
    data: FooterFragment
    socialNetworks: SocialNetworkFragment[]
    paymentIcons: PaymentIconFragment[]
}>()

const hideSwitchers = useIsOnRoute([
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
])
</script>

<style lang="scss" scoped>
.footer {
    width: 100%;
    padding: 0;
    background-color: $stone-3;
    color: $slate;

    .footer-wrapper {
        width: 100%;
        max-width: 768px;
        margin: 0 auto;
        padding: 0 20px 30px;

        @include min-width(1024) {
            max-width: 1440px;
            padding: 50px 20px 30px;
        }
    }

    .footer-navigation {
        display: flex;
        flex-wrap: wrap;

        .switchers {
            display: flex;
            margin: 16px 0 40px;
            padding: 8px 16px;

            border: 1px solid $grey-500;
            border-radius: 8px;

            @include min-width(410) {
                margin: 20px 10px;
            }

            @include min-width(1024) {
                margin: 20px 40px;
            }

            .region-switcher,
            .currency-switcher {
                background-color: transparent;
            }

            .region-switcher {
                margin-left: 16px;
            }
        }
    }

    .footer-bar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        @include min-width(1024) {
            padding: 0 40px;
            margin: 0 0 40px;
        }

        .copyright {
            width: 100%;
            font-family: $effra-font-stack;
            font-size: 0.75rem;
            font-weight: 350;
            line-height: 1.25rem;
            letter-spacing: 0.05rem;
            text-align: center;
            order: 2;

            @include min-width(1024) {
                width: 33%;
                text-align: left;
                order: initial;
            }
        }

        .social {
            width: 100%;
            display: flex;
            justify-content: center;
            margin: 0 0 30px;

            @include min-width(1024) {
                width: 33%;
                margin: 0;
            }
        }

        .payment {
            width: 100%;
            display: flex;
            justify-content: center;
            margin: 0 0 30px;

            @include min-width(1024) {
                justify-content: flex-end;
                width: 33%;
                margin: 0;
            }
        }
    }
}
</style>
